import { z } from 'zod';
import { id, organizationId, timestamp, paginationQuery, paginatedResult } from './common.js';
import { review, reviewWorkspace } from './reputation.js';
export const reviewVisibility = z.enum(['published', 'hidden']);
export const reviewCaseStatus = z.enum(['open', 'in_review', 'resolved', 'withdrawn']);
export const reviewCaseKind = z.enum(['report', 'appeal']);
export const reviewConcern = z.enum([
  'privacy',
  'harassment',
  'inaccurate',
  'conflict_of_interest',
  'other',
]);
export const governedReview = z.object({
  id,
  bookingId: id,
  locationId: id,
  locationName: z.string(),
  professionalName: z.string(),
  authorName: z.string(),
  workspace: reviewWorkspace,
  relationship: z.enum(['authored', 'received']),
  stars: z.number().int().min(1).max(5).nullable(),
  body: z.string().nullable(),
  visibility: reviewVisibility,
  moderationRevision: z.number().int().nonnegative(),
  decisionReason: z.string().nullable(),
  decisionAt: z.string().nullable(),
  createdAt: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  timezone: z.string(),
  canReport: z.boolean(),
  canAppeal: z.boolean(),
  canModerate: z.boolean(),
  allowedDecisions: z.array(z.enum(['keep', 'hide', 'restore'])),
  accessNotice: z.string().nullable(),
});
export type GovernedReview = z.infer<typeof governedReview>;
export const reviewGovernanceQuery = paginationQuery.extend({
  workspace: reviewWorkspace.default('professional'),
  view: z.enum(['received', 'authored']).default('received'),
  visibility: z.enum(['all', 'published', 'hidden']).default('all'),
  search: z.string().trim().max(100).optional(),
});
export type ReviewGovernanceQuery = z.infer<typeof reviewGovernanceQuery>;
export const governedReviewPage = paginatedResult(governedReview);
export const reviewModerationEvent = z.object({
  id,
  reviewId: id,
  revision: z.number().int().positive(),
  action: z.enum(['keep', 'hide', 'restore']),
  reason: z.string(),
  actorName: z.string(),
  createdAt: z.string(),
});
export const governedReviewDetail = z.object({
  review: governedReview,
  history: paginatedResult(reviewModerationEvent),
});
export const reviewCase = z.object({
  id,
  reviewId: id,
  bookingId: id,
  locationName: z.string(),
  kind: reviewCaseKind,
  status: reviewCaseStatus,
  concern: reviewConcern,
  message: z.string(),
  workspace: reviewWorkspace,
  revision: z.number().int().positive(),
  decisionRevision: z.number().int().nonnegative(),
  resolution: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  canReply: z.boolean(),
  canWithdraw: z.boolean(),
  canOperate: z.boolean(),
  allowedOperations: z.array(z.enum(['start_review', 'reply', 'note', 'resolve'])),
  accessNotice: z.string().nullable(),
});
export type ReviewCase = z.infer<typeof reviewCase>;
export const reviewCaseEvent = z.object({
  id,
  caseId: id,
  kind: z.enum(['created', 'reply', 'withdraw', 'start_review', 'resolve', 'note']),
  actorName: z.string(),
  actorSide: z.enum(['participant', 'operator']),
  message: z.string(),
  private: z.boolean(),
  revision: z.number().int().positive(),
  createdAt: z.string(),
});
export const reviewCaseDetail = z.object({
  case: reviewCase,
  review: governedReview,
  events: paginatedResult(reviewCaseEvent),
});
export const reviewCasePage = paginatedResult(reviewCase);
export const reviewCaseQuery = paginationQuery.extend({
  reviewId: id.optional(),
  workspace: reviewWorkspace.default('professional'),
  status: z.enum(['all', 'open', 'in_review', 'resolved', 'withdrawn']).default('all'),
  kind: z.enum(['all', 'report', 'appeal']).default('all'),
  search: z.string().trim().max(100).optional(),
});
export type ReviewCaseQuery = z.infer<typeof reviewCaseQuery>;
export const reviewCaseCreate = z
  .object({
    workspace: reviewWorkspace,
    reviewId: id,
    kind: reviewCaseKind,
    concern: reviewConcern,
    message: z.string().trim().min(10).max(3000),
    expectedModerationRevision: z.number().int().nonnegative(),
    clientRequestId: z.uuid(),
  })
  .strict();
export type ReviewCaseCreate = z.infer<typeof reviewCaseCreate>;
export const reviewCaseMutation = z
  .object({
    action: z.enum(['reply', 'withdraw']),
    message: z.string().trim().max(3000).default(''),
    expectedRevision: z.number().int().positive(),
    clientRequestId: z.uuid(),
  })
  .strict()
  .refine((v) => v.action !== 'reply' || v.message.length >= 1, { message: 'Add a reply.' });
export type ReviewCaseMutation = z.infer<typeof reviewCaseMutation>;
export const reviewModerationInput = z
  .object({
    action: z.enum(['keep', 'hide', 'restore']),
    reason: z.string().trim().min(10).max(2000),
    expectedRevision: z.number().int().nonnegative(),
    clientRequestId: z.uuid(),
  })
  .strict();
export type ReviewModerationInput = z.infer<typeof reviewModerationInput>;
export const reviewCaseOperation = z
  .object({
    action: z.enum(['start_review', 'reply', 'note', 'resolve']),
    message: z.string().trim().min(1).max(3000),
    expectedRevision: z.number().int().positive(),
    clientRequestId: z.uuid(),
  })
  .strict();
export type ReviewCaseOperation = z.infer<typeof reviewCaseOperation>;
export const reviewModerationQueueQuery = paginationQuery.extend({
  visibility: z.enum(['all', 'published', 'hidden']).default('all'),
  search: z.string().trim().max(100).optional(),
});
export type ReviewModerationQueueQuery = z.infer<typeof reviewModerationQueueQuery>;

/** Complete persistence contracts. Participant DTOs deliberately omit private database fields. */
export const reviewRecord = review.extend({
  visibility: reviewVisibility,
  moderationRevision: z.number().int().nonnegative(),
  decisionReason: z.string().nullable(),
  decisionAt: timestamp.nullable(),
  moderationRequestId: id.nullable(),
});
export type ReviewRecord = z.infer<typeof reviewRecord>;

export const reviewModerationEventRecord = reviewModerationEvent.extend({
  organizationId,
  actorUserId: id,
  clientRequestId: id,
  requestJson: z.string(),
});
export type ReviewModerationEventRecord = z.infer<typeof reviewModerationEventRecord>;

export const reviewCaseRecord = reviewCase
  .pick({
    id: true,
    reviewId: true,
    workspace: true,
    kind: true,
    status: true,
    concern: true,
    message: true,
    revision: true,
    decisionRevision: true,
    resolution: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    organizationId,
    ownerUserId: id,
    lastRequestId: id,
  });
export type ReviewCaseRecord = z.infer<typeof reviewCaseRecord>;

export const reviewCaseEventRecord = reviewCaseEvent.extend({
  organizationId,
  actorUserId: id,
  clientRequestId: id,
  requestJson: z.string(),
});
export type ReviewCaseEventRecord = z.infer<typeof reviewCaseEventRecord>;
